import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { getBrowserCultureLang, TranslocoService } from "@jsverse/transloco";
import { AzureAdService } from "@services/azure-ad.service";
import { Subject } from "rxjs";

import { LoadingComponent } from "@components/loading/loading.component";
import { SessionStorageService } from "@core/services/session-storage.service";
import { SessionTimeoutService } from "@core/services/session-timeout.service";
import { NpsComponent } from "@pages/nps/nps.component";
import { ShellComponent } from "@components/shell/shell.component";
import { NotificationService } from "@services/notification.service";
import config from "devextreme/core/config";
import { Auditor } from "./Domain/Models/auditor.model";
import { licenseKey } from "devextreme-license";

@Component({
    selector: "app-root",
    imports: [RouterOutlet, LoadingComponent, NpsComponent, ShellComponent],
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"]
})
export class AppComponent implements OnInit, OnDestroy {
    title = "AIAS";
    private readonly _destroying$ = new Subject<void>();
    menuState: string = "in";
    auditor: Auditor[];
    hideHeader = false;
    windowHeight: number;

    private authService = inject(MsalService);
    private router = inject(Router);
    private azureAdService = inject(AzureAdService);
    private translocoService = inject(TranslocoService);
    private notification = inject(NotificationService);
    private sessionStorage = inject(SessionStorageService);
    private sessionTimeout = inject(SessionTimeoutService);

    isUserVerified = this.sessionStorage.get("isUserVerified");

    // @Inject(MSAL_GUARD_CONFIG)
    // private msalGuardConfig: MsalGuardConfiguration;

    async ngOnInit() {
        // MSAL v3+ exige initialize() antes de qualquer outra API (handleRedirectPromise,
        // enableAccountStorageEvents, ssoSilent...). É idempotente.
        await this.authService.instance.initialize();
        this.authService.instance.enableAccountStorageEvents();

        try {
            this.loadTranslations();

            config({
                licenseKey: licenseKey
            });

            // Processa o retorno de um redirect de login e consolida a conta ativa.
            // setActiveAccount é essencial: sem ele, getActiveAccount() sempre retorna null e o
            // fluxo força loginRedirect mesmo havendo sessão (defeito latente exposto pelo M-05,
            // que migrou o cache do MSAL para sessionStorage, isolado por aba).
            const redirectResult = await this.authService.instance.handleRedirectPromise();
            if (redirectResult?.account) {
                this.authService.instance.setActiveAccount(redirectResult.account);
            }

            this.ensureActiveAccount();

            if (this.authService.instance.getActiveAccount()) {
                // Já há sessão nesta aba (ou acabou de voltar do redirect): verifica o usuário.
                // O caso de aba nova sem conta é tratado no CanActivateGuard (ssoSilent), que é
                // quem decide a navegação para as rotas protegidas.
                // M-06: marca o início do timeout absoluto de sessão (idempotente na mesma aba).
                this.sessionTimeout.startSession();
                this.azureAdService.userVerify();
            }
        } catch (error) {
            if (!this.azureAdService.isCurrentRoutePublic()) {
                this.router.navigate(["/login"]);
                this.notification.serviceError(error);
            }
        }
    }

    /**
     * Define a conta ativa do MSAL a partir das contas em cache quando ainda não há uma ativa.
     * Necessário porque o restante do código depende de getActiveAccount().
     */
    private ensureActiveAccount(): void {
        if (this.authService.instance.getActiveAccount()) return;

        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
        }
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    private loadTranslations() {
        this.translocoService.setAvailableLangs(["pt-BR", "en-US", "es-ES"]);
        const browserLang = getBrowserCultureLang();
        this.translocoService.setActiveLang(browserLang.match(/pt-BR|es-ES|en-US/) ? browserLang : "en-US");
    }
}
